import { useEffect, useState, useCallback, useMemo } from "react";
import { downloadValuationReport, fetchFtmValuations } from "../service";
import {
    capitalizeFirstLetter,
    convertTimestampToFormat,
    formatMoney,
    truncateText
} from "../../../utils";
import { theme } from "../../../theme";

const Report = ({ ftm_id, refreshKey, valuationId }) => {
    const [loading, setLoading] = useState(false);
    const [ftm, setFtm] = useState(null);
    const [company, setCompany] = useState(null);
    const [valuations, setValuations] = useState({ items: [] });

    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const loadFtmValuations = useCallback(async () => {
        if (!ftm_id) return;

        try {
            setLoading(true);

            const response = await fetchFtmValuations(ftm_id, valuationId);

            if (response?.status === "Success") {
                const { valuation_information, ftm, company_details } = response.data;

                setValuations(valuation_information || { items: [] });
                setFtm(ftm || null);
                setCompany(company_details || null);
            }
        } catch (error) {
            console.error("Error fetching FtmValuations:", error);
        } finally {
            setLoading(false);
        }
    }, [valuationId, ftm_id]);

    useEffect(() => {
        loadFtmValuations();
    }, [loadFtmValuations, refreshKey]);

    const paymentPercentage =
        ftm?.payment_terms?.ftm_payment_term_additional_charge_percentage || 0;

    const paymentTermDescription =
        ftm?.payment_terms?.ftm_payment_term_description || null;

    const subtotal = useMemo(() => {
        return valuations.items.reduce(
            (sum, item) =>
                sum + Number(item?.amount || 0),
            0
        );
    }, [valuations.items]);

    const paymentTermCharge = useMemo(() => {
        return subtotal * (paymentPercentage / 100);
    }, [subtotal, paymentPercentage]);

    const finalTotal = useMemo(() => {
        return subtotal - paymentTermCharge;
    }, [subtotal, paymentTermCharge]);

    const rows = useMemo(() => {
        return valuations.items.map((item, index) => (
            <tr key={index}>
                <td className="text-start">
                    {capitalizeFirstLetter(item?.type)}
                </td>
                <td className="text-end ps-4">
                    {formatMoney(item?.amount)}
                </td>
            </tr>
        ));
    }, [valuations.items]);

    const handleDownload = useCallback(async () => {
        try {
            setDownloadBtnLoading(true);
            await downloadValuationReport(valuationId);
        } catch (error) {
            console.error("Error downloading invoice:", error);
        } finally {
            setDownloadBtnLoading(false);
        }
    }, [valuationId]);

    if (loading) return null;

    return (
        <div className="card">
            <div className="card-header text-center">
                <div className="row justify-content-between">
                    <div className="col-md-4 text-start">
                        <img
                            className="mb-2 w-75 p-2"
                            src="/assets/img/instafix-logo-long.png"
                            alt="Logo"
                        />
                        <h6>{company?.address}</h6>
                        <p className="text-secondary">
                            tel: {company?.contact_number}
                        </p>
                    </div>

                    <div className="col-lg-4 col-md-7 text-md-end text-start mt-5">
                        <h6 className="mb-0">
                            {ftm?.forename} {ftm?.surname}
                        </h6>
                        <p className="text-secondary">
                            {ftm?.street_address}
                            <br />
                            {ftm?.place}
                            <br />
                            {ftm?.postcode}
                        </p>
                    </div>
                </div>

                <br />

                <div className="row justify-content-md-between">
                    <div className="col-md-4 mt-1 text-start">
                        <h6 className="text-secondary font-weight-normal mb-0">
                            Valuation no
                        </h6>
                        <h5 className="mb-0">
                            #{valuations?.id}
                        </h5>
                    </div>

                    <div className="col-lg-6 col-md-6 mt-auto">
                        <div className="row mt-1 text-md-end text-start">
                            <div className="col-md-6">
                                <h6 className="text-secondary mb-0">
                                    Valuation date:
                                </h6>
                            </div>
                            <div className="col-md-6">
                                <h6 className="mb-0">
                                    {convertTimestampToFormat(Date.now())}
                                </h6>
                            </div>
                        </div>

                        <div className="row text-md-end text-start">
                            <div className="col-md-6">
                                <h6 className="text-secondary mb-0">
                                    Last valuation:
                                </h6>
                            </div>
                            <div className="col-md-6">
                                <h6 className="mb-0">
                                    {convertTimestampToFormat(
                                        valuations?.timestamp
                                    )}
                                </h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body pb-0">
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="text-start ps-2">Item</th>
                                <th className="text-end ps-2">Amount</th>
                            </tr>
                        </thead>

                        <tbody>{rows}</tbody>

                        <tfoot>
                            {paymentPercentage > 0 && (
                                <>
                                    <tr>
                                        <th className="h6 ps-4">Sub total</th>
                                        <th className="text-end h6 ps-4">
                                            {formatMoney(subtotal)}
                                        </th>
                                    </tr>

                                    <tr>
                                        <th className="h6 ps-4 text-danger">
                                            Payment terms ({paymentPercentage}%)
                                            <p className="text-xs">{truncateText(paymentTermDescription, 60)}</p>
                                        </th>
                                        <th className="text-end h6 ps-4 text-danger">
                                            {formatMoney(paymentTermCharge)}
                                        </th>
                                    </tr>
                                </>
                            )}

                            <tr>
                                <th className="h5 ps-4 text-success">Total</th>
                                <th className="text-end h5 ps-4 text-success">
                                    {formatMoney(finalTotal)}
                                </th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="card-footer pt-0">
                <div className="row">
                    <div className="col-lg-12 text-md-end">
                        <button 
                            className={`btn bg-gradient-${theme.main} mb-0 btn-sm`} 
                            onClick={handleDownload} 
                            type="button" 
                            name="button" 
                            disabled={downloadBtnLoading || valuationId === 0}
                        >
                            {downloadBtnLoading ? "Downloading..." : "Download"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Report;